/**
 * Customer and vehicle input helpers, shared by every form that identifies a car.
 *
 * Extracted from `BookingFormModal` when the quote form needed the same customer
 * search and vehicle picker. They are here rather than in the picker component
 * because `BookingFormModal` still uses them in its other steps — leaving them
 * behind would have meant the picker importing from the booking form, which is the
 * dependency pointing the wrong way.
 *
 * Behaviour is unchanged from the originals; this is a move, not a rewrite.
 */

/**
 * Strip the punctuation people type into a phone number.
 *
 * Comparison only — never store the result. `0412 345 678`, `0412-345-678` and
 * `(0412) 345678` are the same number, and a search that treats them as three
 * different customers creates duplicates on every second booking.
 */
export const normalizePhone = (phone) => (phone ? phone.replace(/[\s\-()]/g, '') : '');

/**
 * Canonical form of a registration: no spaces or hyphens, upper case.
 *
 * `1AB 2CD` and `1ab-2cd` are the same car. Rego is how staff find a vehicle, so a
 * lookup that is case- or space-sensitive silently reports "no vehicle found" for a
 * car that is sitting in the workshop.
 */
export const normalizeRego = (rego) => (rego ? rego.replace(/[\s\-]/g, '').toUpperCase() : '');

/**
 * Customer-since label — short month and year, e.g. "Mar 2024".
 *
 * Returns null for a missing or unparseable timestamp so callers can leave the line
 * out entirely rather than render "Invalid Date" at a customer.
 */
export const fmtCustomerSince = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

/**
 * The "Customer since" label for a customer, from whichever date actually means it.
 *
 * 🔴 `createdAt` is when the ROW was made here, and for a migrated workshop that is the
 * import date — so every customer of twelve years' standing reads as new. The importer
 * fills `customerSince` from the source system's own date; it is null for anyone
 * created in this product, where `createdAt` IS the same fact.
 *
 * Keep the two in this order and in this one place: the next component to show this
 * label will otherwise reach for `createdAt`, which is right until the day it is not.
 */
export const customerSinceOf = (customer) =>
    fmtCustomerSince(customer?.customerSince ?? customer?.createdAt);

/** Australian states and territories, as they appear on a plate. */
export const REGO_STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

/**
 * The `customer` half of a create request, as the server's CustomerInfo expects it.
 *
 * Two shapes, and the difference matters:
 *
 *  - an EXISTING customer is identified by uuid, and carries an email only so the
 *    server can FILL a blank address on the record. It never overwrites one already
 *    there. Customers the voice bot created have no email, and those are exactly the
 *    people a call-back is about — without this their confirmation silently never
 *    sends.
 *  - a NEW customer sends both `name` and `businessName` even though only one is
 *    populated, so the contract is explicit rather than inferred from which field
 *    happens to be absent.
 *
 * Shared by the booking form and the quote form. They must agree: two forms building
 * this differently is how one of them starts creating duplicate customers.
 */
export const buildCustomerPayload = ({ foundCustomer, formData }) => {
    if (foundCustomer?.uuid) {
        return {
            uuid: foundCustomer.uuid,
            email: formData.customerEmail?.trim() ? formData.customerEmail.trim() : null,
        };
    }

    const isBusiness = formData.customerType === 'BUSINESS';
    return {
        customerType: isBusiness ? 'BUSINESS' : 'INDIVIDUAL',
        name: isBusiness ? null : formData.customerName,
        businessName: isBusiness ? formData.customerBusinessName?.trim() : null,
        phone: normalizePhone(formData.customerPhone),
        email: formData.customerEmail?.trim() ?? '',
    };
};

/**
 * The `vehicle` half of a create request, as the server's VehicleInfo expects it.
 *
 * A picked vehicle is just its uuid — edits to an existing vehicle go through the
 * vehicle endpoint, not through here, so sending its details as well would let a
 * create request quietly rewrite a car's record.
 */
export const buildVehiclePayload = ({ selectedVehicleUuid, addingNewVehicle, formData }) => {
    if (selectedVehicleUuid && !addingNewVehicle) {
        return { uuid: selectedVehicleUuid };
    }
    return {
        rego: normalizeRego(formData.vehicleRego),
        regoState: formData.vehicleRegoState || 'VIC',
        make: formData.vehicleMake || null,
        model: formData.vehicleModel || null,
        year: formData.vehicleYear ? parseInt(formData.vehicleYear, 10) : null,
        vin: formData.vehicleVin?.trim() || null,
        engineNumber: formData.vehicleEngineNumber?.trim() || null,
        transmission: formData.vehicleTransmission || null,
    };
};
